import { z } from 'zod'

// Define the allowed values as constant arrays.
const schemaTypes = ['string', 'number', 'boolean', 'object', 'array', 'null'] as const
export const stringSchemaFormats = ['date', 'date-time', 'email', 'uri', 'uuid'] as const
export const numberSchemaFormats = ['int64'] as const

// Derive the TypeScript types from the constant arrays.
export type SchemaType = (typeof schemaTypes)[number]
export type StringSchemaFormat = (typeof stringSchemaFormats)[number]
export type NumberSchemaFormat = (typeof numberSchemaFormats)[number]

// Create Zod enums from the constant arrays.
const SchemaTypeEnum = z.enum(schemaTypes)
const StringSchemaFormatEnum = z.enum(stringSchemaFormats)
const NumberSchemaFormatEnum = z.enum(numberSchemaFormats)

// Subset of JsonSchema7 that are most used in our output schemas
export interface SimpleSchema {
  title: string
  description?: string
  type: SchemaType | SchemaType[] // For a nullable string would be ['string', 'null'], etc
  items?: SimpleSchema // For array types
  properties?: Record<string, SimpleSchema> // For object types
  format?: StringSchemaFormat | NumberSchemaFormat // For formats like date, email, etc.
  required?: string[]
}

// Create the recursive schema using z.lazy.
export const SimpleSchema: z.ZodType<SimpleSchema> = z.lazy(() =>
  z
    .object({
      title: z.string().min(1, 'Title is required and must not be empty'),
      description: z.string().optional(),
      // "type" can be a single SchemaTypeEnum or an array of SchemaTypeEnum values.
      type: z.union([SchemaTypeEnum, z.array(SchemaTypeEnum)]),
      // For "array" types, "items" should be another SimpleSchema.
      items: z.lazy(() => SimpleSchema).optional(),
      // For "object" types, "properties" is a record of SimpleSchema.
      properties: z
        .record(
          z.string(),
          z.lazy(() => SimpleSchema),
        )
        .optional(),
      // "format" is optional and restricted to the specified formats.
      format: z.union([StringSchemaFormatEnum, NumberSchemaFormatEnum]).optional(),
      // "required" is an optional array of strings.
      required: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      // Normalize type into an array for consistency.
      const typeList = Array.isArray(data.type) ? data.type : [data.type]

      // If the schema is not an array, items should not be provided
      if (data.items && !typeList.includes('array')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "'items' can only be used with type 'array'.",
          path: ['items'],
        })
      }

      // If the schema is not an object, properties should not be provided
      if (data.properties && !typeList.includes('object')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "'properties' can only be used with type 'object'.",
          path: ['properties'],
        })
      }

      // If the schema is (or includes) "array", require that "items" is provided.
      if (typeList.includes('array') && !data.items) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "For type 'array', 'items' must be provided.",
          path: ['items'],
        })
      }

      // If the schema is (or includes) "object", require that "properties" is provided.
      if (typeList.includes('object') && !data.properties) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "For type 'object', 'properties' must be provided.",
          path: ['properties'],
        })
      }

      // If "required" is provided, make sure it's only for objects
      if (data.required && !typeList.includes('object')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "The 'required' property can only be used with type 'object'.",
          path: ['required'],
        })
      }

      // If "required" is provided and the type is "object", validate that all required properties exist
      if (data.required && data.properties && typeList.includes('object')) {
        const propertyKeys = Object.keys(data.properties)
        const invalidRequiredProps = data.required.filter((prop) => !propertyKeys.includes(prop))

        if (invalidRequiredProps.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `The following required properties do not exist in 'properties': ${invalidRequiredProps.join(', ')}`,
            path: ['required'],
          })
        }
      }

      // Validate that format is only used with the appropriate type
      if (data.format) {
        // Check if the format is a string format
        const isStringFormat = stringSchemaFormats.some((format) => format === data.format)
        // Check if the format is a number format
        const isNumberFormat = numberSchemaFormats.some((format) => format === data.format)

        // If it's a string format, ensure that 'string' is in the type list
        if (isStringFormat && !typeList.includes('string')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Format '${data.format}' can only be used with type 'string'.`,
            path: ['format'],
          })
        }

        // If it's a number format, ensure that 'number' is in the type list
        if (isNumberFormat && !typeList.includes('number')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Format '${data.format}' can only be used with type 'number'.`,
            path: ['format'],
          })
        }
      }
    }),
)
